import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { batchTextNotesFromFilename } from "../../../api/postCall/batchTextNotesFromFilename";
import { runNycbsPharmaStatusTracking } from "../../../api/postCall/runNycbsPharmaStatusTracking";
import { ModalId } from "../../../enums/modalId";
import { PushToSftpModalModes } from "../../../enums/StringLiterals/pushToSftpModalModes";
import { TableNames } from "../../../enums/tableNames";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";
import { AppDispatch, RootState } from "../../../redux/store/store";
import CustomTable from "../../custom-table/custom-table";
import { onClose } from "./actionHandlers/onClose";
import { onDownloadInternalFiles } from "./actionHandlers/onDownloadInternalFiles";
import { onSendToSftp } from "./actionHandlers/onSendToSftp";
import { onTextNoteClick } from "./actionHandlers/onTextNoteClick";
import { onTrackStatus } from "./actionHandlers/onTrackStatus";
import { useDownloadSftpFile } from "./hooks/useDownloadSftpFile";
import { useTableDataForSftpPush } from "./hooks/useTableDataForSftpPush";
import { getModalTitle } from "./utils/getModalTitle";
import { getSaveButtonText } from "./utils/getSaveButtonText";
import { generatePushToSftpTableData } from "./utils/pushToSftpTableDataGenerator";
import { toggleCheckboxSelection } from "./utils/toggleCheckBoxSelection";
import {
  areAllPageItemsSelected,
  toggleSelectAllOnPage,
} from "./utils/toggleSelectAllOnPage";

const PushToSftpModal = () => {
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);
  const mode = metaData?.mode || "push";
  const showDownloadColumn = metaData?.showDownloadColumn || false;
  const {
    tableData,
    setTableData,
    isLoading,
    showInlineLoader,
    totalCount,
    setEndIndex,
  } = useTableDataForSftpPush(mode);
  const { downloadFile } = useDownloadSftpFile();
  const dispatch = useDispatch<AppDispatch>();
  const [isPushing, setIsPushing] = useState(false);
  const abortTrackStatusTimeout = useRef<(() => void) | null>(null);
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [currentEndIndex, setCurrentEndIndex] = useState(9); // Default to itemsPerPage - 1

  // Handle select all for current page only
  const handleSelectAll = () => {
    toggleSelectAllOnPage(currentStartIndex, currentEndIndex, setTableData);
  };

  // Check if all items on current page are selected
  const isAllPageItemsSelected = useMemo(
    () =>
      areAllPageItemsSelected(tableData, currentStartIndex, currentEndIndex),
    [tableData, currentStartIndex, currentEndIndex],
  );

  const trackStatusFetch = async (signal: AbortSignal, filenames: string[]) =>
    runNycbsPharmaStatusTracking(filenames, signal);

  const textNoteFetch = async (signal: AbortSignal, filenames: string[]) =>
    batchTextNotesFromFilename(filenames, signal);

  const { runFetch: runTrackStatus, abort: abortTrackStatus } =
    useAbortableFetch(trackStatusFetch);

  const { runFetch: runTextNote } = useAbortableFetch(textNoteFetch);

  // Cleanup timeouts on unmount to prevent actions on unmounted component
  useEffect(() => {
    return () => {
      abortTrackStatus();
      if (abortTrackStatusTimeout.current) {
        abortTrackStatusTimeout.current();
      }
    };
  }, [abortTrackStatus]);

  const handleSave = async () => {
    setIsPushing(true);
    try {
      if (mode === PushToSftpModalModes.TRACK) {
        const { promise, abort } = onTrackStatus(
          tableData,
          dispatch,
          runTrackStatus,
        );
        abortTrackStatusTimeout.current = abort;
        await promise;
        abortTrackStatusTimeout.current = null;
      } else if (mode === PushToSftpModalModes.TEXT_NOTE) {
        const { promise } = onTextNoteClick(tableData, dispatch, runTextNote);
        await promise;
      } else if (
        mode === PushToSftpModalModes.DOWNLOAD ||
        mode === PushToSftpModalModes.PUSHED_DOWNLOAD
      ) {
        await onDownloadInternalFiles(tableData, dispatch, setTableData);
      } else {
        await onSendToSftp(tableData, dispatch, setTableData);
      }
    } finally {
      setIsPushing(false);
    }
  };

  // Dynamic text based on mode
  const modalTitle = getModalTitle(mode);
  const saveButtonText = getSaveButtonText(mode, isPushing);

  return (
    <Modal
      dialogId={ModalId.PUSH_TO_SFTP_MODAL}
      title={modalTitle}
      onSave={isPushing ? () => {} : handleSave}
      saveButtonText={saveButtonText}
      disableSave={isPushing || isLoading || tableData.length === 0}
      hideFooter={mode === PushToSftpModalModes.PUSHED_DOWNLOAD}
      cancelText="Cancel"
      onClose={() => {
        abortTrackStatus();
        if (abortTrackStatusTimeout.current) {
          abortTrackStatusTimeout.current();
          abortTrackStatusTimeout.current = null;
        }
        onClose(dispatch, setTableData, () => {});
      }}
      heightPercentage={mode === PushToSftpModalModes.PUSHED_DOWNLOAD ? 70 : 60}
    >
      <div className="sftp-modal-container--table h-full w-full overflow-y-auto">
        {isLoading && tableData.length === 0 ? (
          <div className="flex h-full items-center justify-center gap-2">
            <SpinningLoader />
            <span>Loading...</span>
          </div>
        ) : (
          <CustomTable
            tableHeaders={generatePushToSftpTableData(
              showDownloadColumn,
              mode === PushToSftpModalModes.PUSHED_DOWNLOAD,
            )}
            tableData={tableData}
            tableName={TableNames.PUSH_TO_SFTP_MODAL_TABLE}
            itemsPerPage={10}
            pagesPerView={4}
            hideSearchBar={true}
            count={totalCount}
            totalCount={totalCount}
            toggleCheckboxSelection={
              mode === PushToSftpModalModes.PUSHED_DOWNLOAD
                ? undefined
                : (id) => toggleCheckboxSelection(id, setTableData)
            }
            hideSelectAllCheckbox={
              mode === PushToSftpModalModes.PUSHED_DOWNLOAD
            }
            onSelectAll={
              mode === PushToSftpModalModes.PUSHED_DOWNLOAD
                ? undefined
                : handleSelectAll
            }
            isCheckboxSelected={
              mode === PushToSftpModalModes.PUSHED_DOWNLOAD
                ? false
                : isAllPageItemsSelected
            }
            startIndexOfTable={setCurrentStartIndex}
            endIndexOfTable={(endIdx) => {
              setCurrentEndIndex(endIdx);
              setEndIndex(endIdx);
            }}
            showInLineLoader={showInlineLoader}
            onDownloadIconClick={downloadFile}
          />
        )}
      </div>
    </Modal>
  );
};

export default PushToSftpModal;
